import { ADSChangeEvent, Button, object, Radio, RadioGroup, string } from '@appliedsystems/applied-design-system';
import { AdyenPaymentMethod, PadAgreementServiceType, StoredPaymentMethod } from '@appliedsystems/payments-core';
import React, { useCallback, useState } from 'react';
import { AdyenCheckoutState } from 'src/util/adyen';
import * as yup from 'yup';
import { currencyMap } from '../../../src/constants/constants';
import { usePaymentsTranslation } from '../../../src/hooks/usePaymentsTranslation';
import { Locale } from '../../../src/store/Locale';
import { toIntlFormatHelper } from '../../../src/util/getAmountWithFees';
import { useMakePayment } from '../../hooks/useMakePayment';
import { CheckboxRecaptcha } from '../CheckboxRecaptcha/CheckboxRecaptcha';
import { ErrorMessage } from '../ErrorAlert/ErrorAlert';
import { useHppDataStore } from '../HostedPaymentPageContainer/useHppData';
import { useHppSessionStore } from '../HostedPaymentPageContainer/useHppSession';
import classes from './EftComponent.module.scss';
import { EftComponentForm } from './EftComponentForm';
import { PadAgreementServiceTypeRadio } from './PadAgreementServiceTypeRadio';

type Props = {
  onPaymentSuccess: (state: AdyenCheckoutState) => void;
  onBack: () => void;
  setErrorMessage: (errorMessage: ErrorMessage | undefined) => void;
  storedPaymentMethods?: StoredPaymentMethod[];
  hideSavePaymentMethod?: boolean;
};

export const EftComponent = ({
  onPaymentSuccess,
  setErrorMessage,
  onBack,
  storedPaymentMethods,
  hideSavePaymentMethod = true,
}: Props) => {
  const { makePayment } = useMakePayment(setErrorMessage);
  const { hppData } = useHppDataStore();
  const [disablePayNowButton, setDisablePayNowButton] = useState(false);
  const [recaptchaAlertOpen, setRecaptchaAlertOpen] = useState(false);
  const [onRecaptchaSuccess, setOnRecaptchaSuccess] = useState<(recaptchaToken: string) => Promise<void>>();
  const [selectedMethod, setSelectedMethod] = useState<string | 'new'>(storedPaymentMethods?.length ? '' : 'new'); // StoredPaymentMethod.recurringDetailReference (guid)
  const [padAgreementServiceType, setPadAgreementServiceType] = useState<PadAgreementServiceType>();

  const { hppSession: hppSessionDetail } = useHppSessionStore();
  const { locale } = Locale.useContainer();
  const { t } = usePaymentsTranslation();

  const { paymentTotal } = hppData;

  const formSchema = React.useMemo(
    () =>
      object({
        accountHolderName: string().required(t('ACCOUNT_HOLDER_NAME_REQUIRED')),
        bankNumber: string().required(t('BANK_NUMBER_REQUIRED')).length(3, t('BANK_NUMBER_MUST_BE_3_DIGITS')),
        transitNumber: string().required(t('TRANSIT_NUMBER_REQUIRED')).length(5, t('TRANSIT_NUMBER_MUST_BE_5_DIGITS')),
        accountNumber: string()
          .required(t('ACCOUNT_NUMBER_REQUIRED'))
          .min(7, t('ACCOUNT_NUMBER_MUST_BE_AT_LEAST_7_DIGITS'))
          .max(12, t('ACCOUNT_NUMBER_MUST_BE_AT_MOST_12_DIGITS')),
        reEnterAccountNumber: string()
          .required(t('ACCOUNT_NUMBERS_MUST_MATCH'))
          .oneOf([yup.ref('accountNumber')], t('ACCOUNT_NUMBERS_MUST_MATCH')),
        storePaymentMethod: yup.boolean().default(false),
      }),
    [t],
  );

  const handlePayWithStoredPaymentMethod = useCallback(() => {
    // pay with stored payment method
    const state: AdyenCheckoutState = {
      data: {
        paymentMethod: {
          type: AdyenPaymentMethod.Eft,
        },
        clientStateDataIndicator: false,
      },
      isValid: true,
    };
    const paymentMethod = {
      type: AdyenPaymentMethod.Eft,
      storedPaymentMethodId: selectedMethod,
    };

    setOnRecaptchaSuccess(() => async (recaptchaToken: string) => {
      setRecaptchaAlertOpen(false);
      await makePayment({
        onSuccessCallback: onPaymentSuccess,
        paymentMethod: paymentMethod,
        recaptchaToken: recaptchaToken,
        state: state,
        hppData: hppData,
        storePaymentMethod: false,
        hppSessionDetail: hppSessionDetail,
        padAgreementServiceType,
        paymentTotal: hppData.paymentTotal,
        convenienceFee: hppData.paymentFee,
      });
    });
    setRecaptchaAlertOpen(true);
  }, [hppData, hppSessionDetail, makePayment, onPaymentSuccess, padAgreementServiceType, selectedMethod]);

  const handleOnSubmit = useCallback(
    (values: yup.InferType<typeof formSchema>) => {
      if (!values) {
        return;
      }
      const { accountHolderName, bankNumber, transitNumber, accountNumber, storePaymentMethod } = values;

      const state: AdyenCheckoutState = {
        data: {
          paymentMethod: {
            type: AdyenPaymentMethod.Eft,
            bankAccountNumber: accountNumber,
            bankLocationId: transitNumber,
            bankCode: bankNumber,
            ownerName: accountHolderName,
          },
          storePaymentMethod,
          clientStateDataIndicator: false,
        },
        isValid: true,
      };
      setOnRecaptchaSuccess(() => async (recaptchaToken: string) => {
        setRecaptchaAlertOpen(false);
        await makePayment({
          onSuccessCallback: onPaymentSuccess,
          paymentMethod: state.data.paymentMethod,
          recaptchaToken: recaptchaToken,
          state: state,
          hppData: hppData,
          storePaymentMethod,
          hppSessionDetail: hppSessionDetail,
          padAgreementServiceType,
          paymentTotal: hppData.paymentTotal,
          convenienceFee: hppData.paymentFee,
        });
      });
      setRecaptchaAlertOpen(true);
    },
    [hppData, hppSessionDetail, makePayment, onPaymentSuccess, padAgreementServiceType],
  );

  return (
    <div className={classes.eftComponent}>
      {!!storedPaymentMethods?.length && (
        <RadioGroup
          className="payment-method-radio"
          label={t('SELECT_AN_ACCOUNT')}
          name="direction"
          direction="vertical"
          value={selectedMethod}
          onChange={(e: ADSChangeEvent) => setSelectedMethod(e.target.value)}
        >
          {storedPaymentMethods?.map((m) => (
            <Radio value={m.recurringDetailReference} key={m.recurringDetailReference}>
              <>
                {m.paymentMethodNickname ? `${m.paymentMethodNickname} (` : ''}
                {!!m.bank &&
                  [m.bank.ownerName, m.bank.bankAccountNumber ? `*****${m.bank.bankAccountNumber.slice(-4)}` : '']
                    .filter((x) => x)
                    .join('  -  ')}
                {m.paymentMethodNickname ? `)` : ''}
              </>
            </Radio>
          ))}
          <Radio value="new">{t('ENTER_A_NEW_PAYMENT_METHOD')}</Radio>
        </RadioGroup>
      )}

      <EftComponentForm
        handleOnSubmit={handleOnSubmit}
        selectedMethod={selectedMethod}
        setDisablePayNowButton={setDisablePayNowButton}
        hideSavePaymentMethod={hideSavePaymentMethod}
      >
        <PadAgreementServiceTypeRadio
          padAgreementServiceType={padAgreementServiceType}
          setPadAgreementServiceType={setPadAgreementServiceType}
        />
        <Button
          type="primary"
          submit={selectedMethod === 'new'}
          onClick={
            selectedMethod === 'new'
              ? undefined
              : () => {
                  handlePayWithStoredPaymentMethod();
                }
          }
          disabled={disablePayNowButton || selectedMethod === '' || padAgreementServiceType === undefined}
          className={`${classes.payButton} ${disablePayNowButton ? 'pay__button__disabled' : ''}`}
        >
          {t('PAY')} {toIntlFormatHelper(paymentTotal, currencyMap[locale], locale)}
        </Button>
        <Button type="tertiary" onClick={onBack} className={classes.backButton}>
          {t('BACK')}
        </Button>
      </EftComponentForm>

      <CheckboxRecaptcha
        onError={() => {
          setErrorMessage(['RECAPTCHA_BROWSER_ERROR_WITH_ID']);
          setRecaptchaAlertOpen(false);
        }}
        onSuccess={onRecaptchaSuccess!}
        recaptchaAlertOpen={recaptchaAlertOpen}
        action="purchase"
      />
    </div>
  );
};
